"use client";

import { rpc } from "#rpc";
import { useGeo } from "@/i18n/routing";
import { useQuery } from "@tanstack/react-query";
import { checkProductPreorder } from "./utils";

export const useStock = ({
	displayItemId,
	country,
}: {
	displayItemId: string;
	country: string;
}) => {
	return useQuery({
		queryKey: ["useStock", displayItemId, country],
		queryFn: async () => rpc.products.stock({ displayItemId, country }),
		enabled: !!country,
	});
};

export const useIsPreorder = ({
	centraVariantId,
	displayItemId,
	isBundle,
}: {
	centraVariantId?: string;
	displayItemId: string;
	isBundle: boolean;
}) => {
	const { language, country: selectedCountry } = useGeo();
	const { data: stock } = useStock({
		displayItemId,
		country: selectedCountry ?? "",
	});

	return useQuery({
		queryKey: ["preOrderData", displayItemId, selectedCountry],
		queryFn: async () =>
			rpc.products.preorder({
				displayItemId,
				isBundle,
				country: selectedCountry ?? "",
			}),
		enabled: !!centraVariantId && !!selectedCountry,
		select: ({ preorderData, productsInBundle }) => {
			if (!centraVariantId || !selectedCountry) {
				return false;
			}

			if (productsInBundle) {
				const bundleProductsPreorders = productsInBundle
					.map((product) => {
						return checkProductPreorder({
							centraVariantId: product.centraVariantId,
							stock: product.stock,
							country: selectedCountry,
							preorderData,
							language,
						});
					})
					.filter(Boolean);

				if (bundleProductsPreorders.length === 0) {
					return false;
				}

				const furthestAwayDate = bundleProductsPreorders.reduce(
					(furthest, current) => {
						const furthestDate = new Date(furthest.eta);
						const currentDate = new Date(current.eta);
						return currentDate > furthestDate ? current : furthest;
					},
				);

				return furthestAwayDate;
			}

			if (stock === undefined) {
				return false;
			}

			let stockNumber = 0;
			if (typeof stock === "number") {
				stockNumber = stock;
			} else if (stock === "100+") {
				stockNumber = 100;
			}

			return checkProductPreorder({
				centraVariantId: Number(centraVariantId),
				stock: stockNumber,
				country: selectedCountry,
				preorderData,
				language,
			});
		},
	});
};
